import * as z from 'zod'

/**
 * The stages a Proof run goes through, in order. Mirrors App\Enums\ProofLifecycleStage; the snapshot's
 * `lifecycle.stages` carries one row per stage that occurred, in the order the stages began.
 */
export type ProofLifecycleStage = z.infer<typeof ProofLifecycleStage>
export const ProofLifecycleStage = z.enum([
  'sandbox',
  'checkout',
  'dependencies',
  'services',
  'harness',
  'capture',
  'upload',
  'cleanup',
])

/** A stage on the wire (App\Enums\ProofLifecycleStatus): `running` is a stage that began and has no `end_ms` yet. */
export type ProofLifecycleStatus = z.infer<typeof ProofLifecycleStatus>
export const ProofLifecycleStatus = z.enum(['done', 'running', 'failed'])

/**
 * What the claims went through for one service of the services stage (App\Enums\ProofSetupServiceState):
 * the real thing, a stand-in, or nothing.
 */
export type ProofSetupServiceState = z.infer<typeof ProofSetupServiceState>
export const ProofSetupServiceState = z.enum(['live', 'mocked', 'not_run'])

/**
 * Why a mocked or not_run service was not live (App\Enums\ProofSetupReason). `missing_key` carries the keys in
 * `missing_env_vars`, `configuration` the setting in `setting`; every reason but `missing_key` carries `because`.
 * `not_exercised` is a not_run service no claim goes through, so its `claims` are empty.
 */
export type ProofSetupReason = z.infer<typeof ProofSetupReason>
export const ProofSetupReason = z.enum([
  'missing_key',
  'configuration',
  'service_unavailable',
  'time_budget',
  'not_needed',
  'not_exercised',
  'other',
])

export type ProofSetupRemediationKind = z.infer<typeof ProofSetupRemediationKind>
export const ProofSetupRemediationKind = z.enum(['request_env_vars', 'retry_proof'])
export const ENV_VAR_REQUEST_MAX_KEYS = 10

export type ProofSetupRemediation = z.infer<typeof ProofSetupRemediation>
export const ProofSetupRemediation = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal(ProofSetupRemediationKind.enum.request_env_vars),
    keys: z
      .array(z.string().regex(/^[A-Za-z_][A-Za-z0-9_]{0,127}$/))
      .min(1)
      .max(ENV_VAR_REQUEST_MAX_KEYS),
  }),
  z.object({
    kind: z.literal(ProofSetupRemediationKind.enum.retry_proof),
    cause: z.literal(ProofSetupReason.enum.time_budget),
  }),
])
